import React, { useState, useRef, useMemo, useEffect } from 'react';
import {
  ListenerAudio,
  useUpdateListenerAudio,
  useGradeAudioMutation,
  EXPERIENCE_GRADE,
  getPipelineStatusColor,
  getPipelineStatusLabel,
} from '../../../../hooks/useListenerAudio';
import AudioPlayer from '../../../../components/common/audio-player-slider';
import { toast } from 'react-toastify';
import TagSelectorModal from '../../../../components/content-library/tag-selector-modal';
import { use2026TagGroups } from '../../../../hooks/useTagGroups';
import { Tag } from '../../../../hooks/useTags';
import { listenerRejectTemplateMessages, SystemMessage, useSystemMessages } from '../../../../hooks/useComms';
import Modal from '../../../../components/common/modal';

import AuthenticatedVideo from '../../../../components/common/authenticated-video';
import { useChunkedUpload } from '../../../../hooks/useChunkedUpload';

const messageQualityToGrade = (quality: number): 'approved' | 'rejected' | 'ungraded' =>
  quality === EXPERIENCE_GRADE.approved ? 'approved' : quality === EXPERIENCE_GRADE.rejected ? 'rejected' : 'ungraded';

const resolveTitle = (experience: ListenerAudio): string => experience.peer_title || experience.title || '';

interface RejectModalData {
  templateId: number | null;
  message: string;
  sendMessage: boolean;
}

type SavePayload = {
  title?: string;
  ai_title?: string;
  ai_summary?: string;
  transcription?: string;
  video_id?: string;
};

interface FieldTextareaProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  rows?: number;
  placeholder?: string;
  showCount?: boolean;
}

const FieldTextarea: React.FC<FieldTextareaProps> = ({ label, value, onChange, rows = 3, placeholder, showCount }) => (
  <div className="rounded-lg border border-gray-200 bg-white p-4">
    <label className="mb-2 block text-sm font-semibold text-gray-700">{label}</label>
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
      rows={rows}
      placeholder={placeholder}
    />
    {showCount && <p className="mt-1 text-right text-xs text-gray-500">{value.length} characters</p>}
  </div>
);

interface ExperienceDetailViewProps {
  experience: ListenerAudio;
  experienceType: 'audio' | 'video';
  refetchExperiences: () => void;
  onGraded?: () => void;
  onUploaded?: () => void;
  onDirtyChange?: (isDirty: boolean) => void;
}

const ExperienceDetailView: React.FC<ExperienceDetailViewProps> = ({
  experience,
  experienceType,
  refetchExperiences,
  onGraded,
  onUploaded,
  onDirtyChange,
}) => {
  const [selectedGrade, setSelectedGrade] = useState(() => messageQualityToGrade(experience.message_quality));
  const [peerTitle, setPeerTitle] = useState(() => resolveTitle(experience));
  const [aiTitle, setAiTitle] = useState(experience.ai_title || '');
  const [aiSummary, setAiSummary] = useState(experience.ai_summary || '');
  const [transcription, setTranscription] = useState(experience.transcription || '');
  const [vimeoId, setVimeoId] = useState(experience.video_id || '');
  const [vimeoIdError, setVimeoIdError] = useState('');

  const savedGradeRef = useRef(messageQualityToGrade(experience.message_quality));
  const savedPeerTitleRef = useRef(resolveTitle(experience));
  const savedAiTitleRef = useRef(experience.ai_title || '');
  const savedAiSummaryRef = useRef(experience.ai_summary || '');
  const savedTranscriptionRef = useRef(experience.transcription || '');
  const savedVimeoIdRef = useRef(experience.video_id || '');

  useEffect(() => {
    const isDirty =
      selectedGrade !== savedGradeRef.current ||
      peerTitle !== savedPeerTitleRef.current ||
      aiTitle !== savedAiTitleRef.current ||
      aiSummary !== savedAiSummaryRef.current ||
      transcription !== savedTranscriptionRef.current ||
      vimeoId !== savedVimeoIdRef.current;
    onDirtyChange?.(isDirty);
  }, [selectedGrade, peerTitle, aiTitle, aiSummary, transcription, vimeoId, onDirtyChange]);
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>(experience.topic_tag_ids || []);
  const [showTagsModal, setShowTagsModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showTroubleshooting, setShowTroubleshooting] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [rejectModalData, setRejectModalData] = useState<RejectModalData>({
    templateId: null,
    message: '',
    sendMessage: true,
  });
  const [pendingSavePayload, setPendingSavePayload] = useState<SavePayload | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const { mutate: gradeAudio, isLoading: isGrading } = useGradeAudioMutation();
  const { mutate: updateAudio, isLoading: isUpdating } = useUpdateListenerAudio();
  const {
    upload: chunkedUpload,
    progress: uploadProgress,
    cancel: cancelUpload,
    isUploading: isUploadingFile,
  } = useChunkedUpload();
  const { allTags: tagGroupTags } = use2026TagGroups();
  const { data: systemMessages } = useSystemMessages();

  const rejectedMessages = useMemo(() => listenerRejectTemplateMessages(systemMessages), [systemMessages]);

  const resolvedTagNames = useMemo<string[]>(() => {
    if (experience.tag_names && experience.tag_names.length > 0) return experience.tag_names;
    if (selectedTagIds.length === 0) return [];
    if (tagGroupTags.length > 0) {
      const names = selectedTagIds
        .map((id) => tagGroupTags.find((t: Tag) => t.id === id)?.name)
        .filter((name): name is string => Boolean(name));
      if (names.length > 0) return names;
    }
    return selectedTagIds.map((id) => `#${id}`);
  }, [experience.tag_names, selectedTagIds, tagGroupTags]);

  const validateVimeoId = (value: string): string => {
    if (!value) return '';
    if (!/^\d+$/.test(value)) return 'Vimeo ID must contain numbers only';
    if (value.length !== 10) return 'Vimeo ID must be exactly 10 digits';
    return '';
  };

  const handleVimeoIdChange = (value: string) => {
    setVimeoId(value);
    setVimeoIdError(validateVimeoId(value));
  };

  const clearDirtyRefs = () => {
    savedGradeRef.current = selectedGrade;
    savedPeerTitleRef.current = peerTitle;
    savedAiTitleRef.current = aiTitle;
    savedAiSummaryRef.current = aiSummary;
    savedTranscriptionRef.current = transcription;
    savedVimeoIdRef.current = vimeoId;
    onDirtyChange?.(false);
  };

  const submitGrade = () => {
    const { templateId, message, sendMessage } = rejectModalData;
    const experienceData = {
      grade: selectedGrade,
      systemMessageGroup: 'listener_message_rejected',
      messageId: sendMessage && templateId ? String(templateId) : 'none',
      message: sendMessage && templateId ? message : '',
      transcription: transcription || experience.transcription || '',
      topicsUpdated: false,
      gradeUpdated: true,
    };

    gradeAudio(
      { listenerId: experience.listener_role_id, audioId: experience.id, experienceData, experienceType },
      {
        onSuccess: () => {
          clearDirtyRefs();
          setShowRejectModal(false);
          onGraded ? onGraded() : refetchExperiences();
        },
        onError: () => toast.error(`Failed to grade experience`),
      },
    );
  };

  const handleSave = () => {
    if (experienceType === 'video' && vimeoId) {
      const error = validateVimeoId(vimeoId);
      if (error) {
        setVimeoIdError(error);
        toast.error(error);
        return;
      }
    }

    const payload = {
      title: peerTitle !== resolveTitle(experience) ? peerTitle : undefined,
      ai_title: aiTitle !== (experience.ai_title || '') ? aiTitle : undefined,
      ai_summary: aiSummary !== (experience.ai_summary || '') ? aiSummary : undefined,
      transcription: transcription !== (experience.transcription || '') ? transcription : undefined,
      video_id: vimeoId !== (experience.video_id || '') ? vimeoId : undefined,
    };

    const gradeChanged = selectedGrade !== messageQualityToGrade(experience.message_quality);

    if (gradeChanged && selectedGrade === 'rejected') {
      const firstMsg = rejectedMessages[0];
      setRejectModalData({
        templateId: firstMsg?.id ?? null,
        message: firstMsg?.message ?? '',
        sendMessage: true,
      });
      setPendingSavePayload(payload);
      setShowRejectModal(true);
    } else {
      updateAudio(
        { listenerId: experience.listener_role_id, audioId: experience.id, payload },
        {
          onSuccess: () => {
            if (gradeChanged) {
              // approved or ungraded — no message sent
              const experienceData = {
                grade: selectedGrade,
                systemMessageGroup: 'listener_message_rejected',
                messageId: 'none',
                message: '',
                transcription: transcription || experience.transcription || '',
                topicsUpdated: false,
                gradeUpdated: true,
              };
              gradeAudio(
                { listenerId: experience.listener_role_id, audioId: experience.id, experienceData, experienceType },
                {
                  onSuccess: () => {
                    clearDirtyRefs();
                    onGraded ? onGraded() : refetchExperiences();
                  },
                  onError: () => toast.error('Failed to grade experience'),
                },
              );
            } else {
              clearDirtyRefs();
              toast.success('Experience saved');
              refetchExperiences();
            }
          },
          onError: () => toast.error('Failed to save experience'),
        },
      );
    }
  };

  const handleSaveTags = (newTagIds: number[]) => {
    setSelectedTagIds(newTagIds);
    setShowTagsModal(false);
    updateAudio(
      {
        listenerId: experience.listener_role_id,
        audioId: experience.id,
        payload: { topic_tag_ids: newTagIds },
      },
      {
        onError: () => {
          setSelectedTagIds(experience.topic_tag_ids || []);
          toast.error('Failed to save tags');
        },
      },
    );
  };

  const sanitizeFilename = (name: string) =>
    name
      .replace(/[<>:"/\\|?*]/g, '_')
      .replace(/\s+/g, '_')
      .replace(/_{2,}/g, '_')
      .trim();

  const getFileExtension = (url: string): string =>
    url.match(/\.([a-zA-Z0-9]+)(?:\?|#|$)/)?.[1]?.toLowerCase() ?? (experienceType === 'video' ? 'mp4' : 'mp3');

  const getMimeType = (url: string): string => {
    const ext = getFileExtension(url);
    const map: Record<string, string> = {
      mp4: 'video/mp4',
      mov: 'video/quicktime',
      avi: 'video/x-msvideo',
      webm: 'video/webm',
      mkv: 'video/x-matroska',
      mp3: 'audio/mpeg',
      m4a: 'audio/mp4',
      ogg: 'audio/ogg',
      wav: 'audio/wav',
    };
    return map[ext] ?? (experienceType === 'video' ? 'video/mp4' : 'audio/mpeg');
  };

  const handleDownload = async () => {
    if (!experience.file_url) return;
    setIsDownloading(true);
    setDownloadProgress(0);
    const baseName = resolveTitle(experience) || experience.ai_title || `experience-${experience.id}`;
    const filename = `${sanitizeFilename(baseName)}.${getFileExtension(experience.file_url)}`;
    try {
      const response = await fetch(experience.file_url);
      if (!response.ok) throw new Error('Download failed');
      const contentLength = Number(response.headers.get('Content-Length') || 0);
      const reader = response.body!.getReader();

      if ('showSaveFilePicker' in window) {
        const handle = await (window as any).showSaveFilePicker({ suggestedName: filename });
        const writable = await handle.createWritable();
        let received = 0;
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          await writable.write(value);
          received += value.byteLength;
          if (contentLength > 0) {
            setDownloadProgress(Math.round((received / contentLength) * 100));
          }
        }
        await writable.close();
      } else {
        const chunks: Uint8Array[] = [];
        let received = 0;
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          chunks.push(value);
          received += value.byteLength;
          if (contentLength > 0) {
            setDownloadProgress(Math.round((received / contentLength) * 100));
          }
        }
        const blob = new Blob(chunks);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);
      }
      toast.success('Downloaded successfully');
    } catch (err: any) {
      if (err?.name === 'AbortError') return;
      toast.error('Failed to download. Please try again.');
    } finally {
      setIsDownloading(false);
      setDownloadProgress(0);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (fileInputRef.current) fileInputRef.current.value = '';

    try {
      await chunkedUpload(file, {
        listener_audio_id: experience.id,
        upload_source: experienceType === 'video' ? 'SB_VIDEO_UPLOAD' : 'SB_AUDIO_UPLOAD',
      });
      onUploaded ? onUploaded() : refetchExperiences();
    } catch {
      toast.error('Failed to upload file');
    }
  };

  const pipelineStatus = experience.pipeline_run?.status;
  const pipelineBlocksReview = pipelineStatus === 'processing';
  const pipelineFailed = pipelineStatus === 'failed';
  const pipelineRunId = experience.pipeline_run?.run_id;
  const pipelineStepLabel = experience.pipeline_run?.current_step?.replace(/_/g, ' ');

  const canSave = !isUpdating && !isGrading && !vimeoIdError && !pipelineBlocksReview;

  return (
    <div className="space-y-4">
      {pipelineBlocksReview && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
          <span className="font-semibold">Processing</span>
          {pipelineStepLabel ? ` — ${pipelineStepLabel}…` : ''}. Approve and save are disabled until the enrichment
          pipeline finishes.
        </div>
      )}
      {pipelineFailed && pipelineRunId && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <span className="font-semibold">Review blocked</span> — the enrichment pipeline failed.{' '}
          <a href={`/pipelines?audio=${experience.id}`} className="font-medium underline">
            View failed pipeline run #{pipelineRunId}
          </a>
        </div>
      )}
      <div className="sticky top-0 z-10 -mx-4 border-b border-gray-200 bg-blue-50 px-4 py-3">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-gray-900">Save selected experience</p>
            <p className="text-xs text-gray-500">Experience #{experience.id}</p>
          </div>
          <button
            type="button"
            onClick={handleSave}
            disabled={!canSave}
            className="shrink-0 rounded border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-50 disabled:opacity-50"
            title={vimeoIdError || undefined}
          >
            {isUpdating || isGrading ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {/* Player Card */}
      <div className="rounded-lg border border-gray-200 bg-white p-6">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <p className="font-semibold text-gray-900">
              {peerTitle || aiTitle || <span className="italic text-gray-400">No title</span>}
            </p>
            <span className="text-sm text-gray-500">
              by {experience.listener_role?.user?.first_name || experience.user?.first_name}{' '}
              {experience.listener_role?.user?.last_name || experience.user?.last_name}
            </span>
          </div>
          <div className="flex items-center gap-3">
            {pipelineBlocksReview && (
              <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">Processing</span>
            )}
            {pipelineFailed && (
              <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                Review blocked
              </span>
            )}
            <span className="text-sm text-gray-500">#{experience.id}</span>
          </div>
        </div>

        {/* Audio/Video Player */}
        <div className="mb-4 rounded-lg border border-gray-300 bg-gray-50 p-4">
          {experienceType === 'video' ? (
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 md:items-start">
              {/* Left: uploaded file + download / upload */}
              <div className="flex min-w-0 flex-col gap-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Uploaded File</p>
                {experience.file_url ? (
                  <div className="flex justify-start">
                    <div className="aspect-[9/16] w-full max-w-[280px] overflow-hidden rounded bg-black">
                      <AuthenticatedVideo
                        fileUrl={experience.file_url}
                        mimeType={getMimeType(experience.file_url)}
                        className="h-full w-full object-contain"
                      />
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No uploaded file yet.</p>
                )}
                <div className="flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={handleDownload}
                    disabled={isDownloading || !experience.file_url}
                    className="rounded border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    {isDownloading ? `Downloading ${downloadProgress}%` : 'Download'}
                  </button>
                  <button
                    type="button"
                    onClick={handleUploadClick}
                    disabled={isUpdating || isUploadingFile}
                    className="rounded border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    {isUploadingFile ? 'Uploading...' : 'Upload'}
                  </button>
                </div>
              </div>

              {/* Right: Vimeo embed + ID / watch link */}
              <div className="flex min-w-0 flex-col gap-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Vimeo</p>
                {vimeoId && !vimeoIdError ? (
                  <div className="flex justify-start">
                    <div className="relative aspect-[9/16] w-full max-w-[280px] overflow-hidden rounded bg-black">
                      <iframe
                        key={vimeoId}
                        src={`https://player.vimeo.com/video/${vimeoId}`}
                        className="absolute inset-0 h-full w-full"
                        allow="autoplay; fullscreen; picture-in-picture"
                        allowFullScreen
                        title="Vimeo player"
                      />
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">Enter a valid Vimeo ID below to preview.</p>
                )}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700" htmlFor="experience-vimeo-id">
                    Vimeo ID
                  </label>
                  <div className="flex flex-wrap items-center gap-2">
                    <input
                      id="experience-vimeo-id"
                      type="text"
                      value={vimeoId}
                      onChange={(e) => handleVimeoIdChange(e.target.value)}
                      placeholder="10-digit Vimeo ID"
                      maxLength={10}
                      className={`min-w-0 max-w-xs flex-1 rounded border px-3 py-1.5 text-sm focus:outline-none ${
                        vimeoIdError
                          ? 'border-red-500 bg-red-50 focus:border-red-500'
                          : 'border-gray-300 focus:border-blue-500'
                      }`}
                    />
                    {vimeoId && !vimeoIdError && (
                      <a
                        href={`https://vimeo.com/${vimeoId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="shrink-0 text-sm font-semibold text-blue-600 hover:underline"
                      >
                        Watch on Vimeo ↗
                      </a>
                    )}
                  </div>
                  {vimeoIdError && <p className="text-xs font-medium text-red-600">{vimeoIdError}</p>}
                </div>
              </div>
            </div>
          ) : (
            <AudioPlayer
              audioUrl={experience.file_url}
              audioLength={experience.duration > 86400 ? experience.duration / 1000 : experience.duration}
            />
          )}
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept={experienceType === 'audio' ? 'audio/*,.mp4,.m4a' : 'video/*'}
          onChange={handleFileUpload}
          className="hidden"
        />

        {/* Action Buttons Row (video: download/upload live under upload column; audio: here) */}
        <div className="flex items-center gap-3">
          {experienceType === 'audio' && (
            <>
              <button
                type="button"
                onClick={handleDownload}
                disabled={isDownloading || !experience.file_url}
                className="rounded border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                {isDownloading ? `Downloading ${downloadProgress}%` : 'Download'}
              </button>

              <button
                type="button"
                onClick={handleUploadClick}
                disabled={isUpdating || isUploadingFile}
                className="rounded border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                {isUploadingFile ? `Uploading ${uploadProgress.percent}%` : 'Upload'}
              </button>
              {isUploadingFile && (
                <button onClick={cancelUpload} className="text-sm text-red-500 hover:underline">
                  Cancel
                </button>
              )}
            </>
          )}

          <div className="flex-1" />

          <select
            value={selectedGrade}
            onChange={(e) => setSelectedGrade(e.target.value as 'approved' | 'rejected' | 'ungraded')}
            disabled={isGrading || isUpdating}
            className="min-w-[140px] cursor-pointer rounded border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-900 hover:bg-gray-50 focus:border-blue-500 focus:outline-none disabled:opacity-50"
          >
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="ungraded">Ungraded</option>
          </select>
        </div>
      </div>

      {/* Content Sections */}
      <div className="space-y-4">
        <FieldTextarea
          label="Title"
          value={peerTitle}
          onChange={setPeerTitle}
          rows={2}
          placeholder="Peer-submitted title"
        />
        <FieldTextarea
          label="AI Title"
          value={aiTitle}
          onChange={setAiTitle}
          rows={2}
          placeholder="AI-generated title"
        />
        <FieldTextarea
          label="AI Summary"
          value={aiSummary}
          onChange={setAiSummary}
          rows={4}
          placeholder="AI-generated summary"
        />
        <FieldTextarea
          label="Transcription"
          value={transcription}
          onChange={setTranscription}
          rows={6}
          placeholder="Full transcription"
        />

        {/* Topic Tags */}
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <div className="mb-2 flex items-center justify-between">
            <label className="block text-sm font-semibold text-gray-700">Topic Tags</label>
            <button
              onClick={() => setShowTagsModal(true)}
              className="rounded bg-blue-600 px-3 py-1 text-xs font-semibold text-white hover:bg-blue-700"
            >
              Add Tag
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {resolvedTagNames.length > 0 ? (
              resolvedTagNames.map((name, index) => (
                <span key={index} className="rounded bg-blue-100 px-3 py-1 text-sm text-blue-800">
                  {name}
                </span>
              ))
            ) : (
              <span className="text-sm text-gray-500">No tags assigned</span>
            )}
          </div>
        </div>

        {/* Metadata */}
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <label className="mb-2 block text-sm font-semibold text-gray-700">Metadata</label>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <span className="text-gray-600">Plays:</span>
              <span className="ml-2 font-semibold">{experience.experience_counter || 0}</span>
            </div>
            <div>
              <span className="text-gray-600">Favorites:</span>
              <span className="ml-2 font-semibold">{experience.favorite_count || 0}</span>
            </div>
            <div>
              <span className="text-gray-600">Pipeline status:</span>
              <span className={`ml-2 font-semibold ${getPipelineStatusColor(experience)}`}>
                {getPipelineStatusLabel(experience)}
              </span>
            </div>
          </div>
        </div>

        {/* Troubleshooting */}
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
          <button
            className="flex w-full items-center justify-between text-sm font-semibold text-gray-700"
            onClick={() => setShowTroubleshooting((prev) => !prev)}
          >
            <span>Troubleshooting Information</span>
            <span
              className="ml-2 transition-transform duration-200"
              style={{ display: 'inline-block', transform: showTroubleshooting ? 'rotate(180deg)' : 'rotate(0deg)' }}
            >
              ▼
            </span>
          </button>
          {showTroubleshooting && (
            <div className="mt-3 space-y-2 text-sm text-gray-700">
              <div className="flex justify-between">
                <span>Failed Audio Conversion:</span>
                <span
                  className={experience.ai_audio_conversion_failed ? 'text-red font-medium' : 'text-green font-medium'}
                >
                  {experience.ai_audio_conversion_failed ? 'Yes' : 'No'}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Could not process AI Enrichment:</span>
                <span className={experience.ai_processing_failed ? 'text-red font-medium' : 'text-green font-medium'}>
                  {experience.ai_processing_failed ? 'Yes' : 'No'}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Upload Source:</span>
                <span>{experience.upload_source || 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span>Submitted On:</span>
                <span>{experience.created_at ? new Date(experience.created_at).toLocaleString() : 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span>Graded By:</span>
                <span className="font-medium">{experience.graded_by_user?.email_address || 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span>Graded On:</span>
                <span className="font-medium">
                  {experience.graded_at ? new Date(experience.graded_at).toLocaleString() : 'N/A'}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {showTagsModal && (
        <TagSelectorModal
          title="Select Tags"
          tags={tagGroupTags}
          selectedTagIds={selectedTagIds}
          maxSelectable={5}
          onSave={handleSaveTags}
          onClose={() => setShowTagsModal(false)}
        />
      )}

      {showRejectModal && (
        <Modal
          isModel={showRejectModal}
          InnerComponent={
            <div
              className="relative w-full max-w-lg rounded-lg bg-white shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="max-h-[80vh] overflow-y-auto p-6">
                <h3 className="mb-4 text-lg font-bold text-gray-900">Reject Experience</h3>

                <div className="mb-4">
                  <label className="mb-1 block text-sm font-medium text-gray-700">Rejection Message Template</label>
                  <select
                    className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={rejectModalData.templateId ?? 'none'}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === 'none') {
                        setRejectModalData((prev) => ({ ...prev, templateId: null, message: '', sendMessage: false }));
                      } else {
                        const id = Number(val);
                        const msg = rejectedMessages.find((m: SystemMessage) => m.id === id);
                        setRejectModalData((prev) => ({
                          ...prev,
                          templateId: id,
                          message: msg?.message ?? '',
                          sendMessage: true,
                        }));
                      }
                    }}
                  >
                    <option value="none">Do NOT send message</option>
                    {rejectedMessages.map((m: SystemMessage) => (
                      <option key={m.id} value={m.id}>
                        {m.name}
                      </option>
                    ))}
                  </select>
                </div>

                {rejectModalData.sendMessage && (
                  <div className="mb-6">
                    <label className="mb-1 block text-sm font-medium text-gray-700">Message</label>
                    <textarea
                      className="h-32 w-full rounded border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={rejectModalData.message}
                      onChange={(e) => setRejectModalData((prev) => ({ ...prev, message: e.target.value }))}
                    />
                  </div>
                )}

                <div className="flex justify-end gap-3 border-t border-gray-100 pt-4">
                  <button
                    className="rounded border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    onClick={() => setShowRejectModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    disabled={isGrading || isUpdating}
                    className="bg-red rounded px-4 py-2 text-sm font-semibold text-white opacity-100 hover:opacity-90 disabled:opacity-50"
                    onClick={() => {
                      if (pendingSavePayload) {
                        updateAudio(
                          {
                            listenerId: experience.listener_role_id,
                            audioId: experience.id,
                            payload: pendingSavePayload,
                          },
                          {
                            onSuccess: () => submitGrade(),
                            onError: () => toast.error('Failed to save experience'),
                          },
                        );
                      } else {
                        submitGrade();
                      }
                    }}
                  >
                    {isGrading || isUpdating ? 'Saving...' : 'Save & Reject'}
                  </button>
                </div>
              </div>
            </div>
          }
        />
      )}
    </div>
  );
};

export default ExperienceDetailView;
